// packages/shared/src/schemas/treatment.schema.ts

import { z } from 'zod';
import { CONCERN_KEYS } from '../concerns';
import { ROUTINE_SLOTS } from '../constants';

const concernSchema = z.enum(CONCERN_KEYS as [string, ...string[]]);

export const treatmentTargetSchema = z.object({
  concern: concernSchema,
  /** The clinic's own judgement about when this is worth offering. */
  minSeverity: z.number().int().min(0).max(4),
  effectiveness: z.number().min(0).max(1),
});

export const contraindicationsSchema = z.object({
  pregnancy: z.boolean().optional(),
  breastfeeding: z.boolean().optional(),
  isotretinoin: z.boolean().optional(),
  activeAcne: z.boolean().optional(),
  recentSunExposure: z.boolean().optional(),
  /** Filling a lax lower lid risks malar oedema. */
  lowerLidLaxity: z.boolean().optional(),
});

export const treatmentSchema = z.object({
  name: z.string().min(2).max(120),
  category: z.string().min(2).max(60),
  description: z.string().max(2000).optional(),
  price: z.number().min(0),
  currency: z.string().length(3).optional(),
  sessions: z.number().int().min(1).max(52).default(1),
  intervalDays: z.number().int().min(0).max(365).default(0),
  maintenanceEveryDays: z.number().int().min(1).max(730).optional(),
  durationMinutes: z.number().int().min(5).max(600).optional(),
  downtime: z.string().max(60).optional(),
  bookingUrl: z.string().url().optional(),
  /** The clinic's own id, substituted into deep links. */
  externalId: z.string().max(120).optional(),
  // A first-class column rather than a JSON flag: it is queried, and it is the
  // one safety gate the scan can actually observe.
  fitzpatrickMax: z.number().int().min(1).max(6).optional(),
  minAgeYears: z.number().int().min(0).max(99).optional(),
  contraindications: contraindicationsSchema.optional(),
  isActive: z.boolean().optional(),
  priority: z.number().min(0).max(5).optional(),
  targets: z.array(treatmentTargetSchema).max(20).optional(),
});

export const productTargetSchema = z.object({
  concern: concernSchema,
  effectiveness: z.number().min(0).max(1),
});

export const productSchema = z.object({
  name: z.string().min(2).max(120),
  brand: z.string().max(80).optional(),
  routineSlot: z.enum(ROUTINE_SLOTS as unknown as [string, ...string[]]),
  description: z.string().max(2000).optional(),
  price: z.number().min(0),
  currency: z.string().length(3).optional(),
  imageUrl: z.string().url().optional(),
  externalUrl: z.string().url().optional(),
  shopifyVariantId: z.string().max(40).optional(),
  ingredients: z.array(z.string().max(80)).max(60).optional(),
  // null means "not tracked", which is not the same as zero — treating
  // untracked as out of stock would empty every routine.
  stock: z.number().int().min(0).nullable().optional(),
  priority: z.number().min(0).max(100).optional(),
  targets: z.array(productTargetSchema).max(20).optional(),
});

export type TreatmentInput = z.infer<typeof treatmentSchema>;
export type ProductInput = z.infer<typeof productSchema>;
export type TreatmentTargetInput = z.infer<typeof treatmentTargetSchema>;
